import { useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, Check, X, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Makes a photographed page look scanned: greyscale, whiter paper, darker ink.
function enhance(ctx, w, h) {
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
        const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        const v = clamp((g - 60) * 1.6, 0, 255);
        d[i] = d[i + 1] = d[i + 2] = v;
    }
    ctx.putImageData(img, 0, 0);
}

function CropBox({ box, setBox, wrapRef }) {
    const drag = (corner) => (e) => {
        e.preventDefault();
        const move = (ev) => {
            const r = wrapRef.current.getBoundingClientRect();
            const p = ev.touches ? ev.touches[0] : ev;
            const x = clamp((p.clientX - r.left) / r.width, 0, 1);
            const y = clamp((p.clientY - r.top) / r.height, 0, 1);
            setBox((b) => (corner === "tl" ? { ...b, x0: Math.min(x, b.x1 - 0.1), y0: Math.min(y, b.y1 - 0.1) } : { ...b, x1: Math.max(x, b.x0 + 0.1), y1: Math.max(y, b.y0 + 0.1) }));
        };
        const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };
    const style = { left: `${box.x0 * 100}%`, top: `${box.y0 * 100}%`, width: `${(box.x1 - box.x0) * 100}%`, height: `${(box.y1 - box.y0) * 100}%` };
    const handle = "absolute w-7 h-7 rounded-full bg-white border-4 border-klein touch-none cursor-grab";
    return (
        <div className="absolute border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" style={style} data-testid="scanner-crop-box">
            <span onPointerDown={drag("tl")} className={handle} style={{ left: -14, top: -14 }} data-testid="scanner-crop-tl" />
            <span onPointerDown={drag("br")} className={handle} style={{ right: -14, bottom: -14 }} data-testid="scanner-crop-br" />
        </div>
    );
}

// In-app document scanner: live back camera → snap → crop → scanned look → returns a JPEG File.
export function DocumentScanner({ open, onClose, onCapture, onNativeCamera }) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const wrapRef = useRef(null);
    const [shot, setShot] = useState(null);
    const [preview, setPreview] = useState("");
    const [ready, setReady] = useState(false);
    const [error, setError] = useState("");
    const [scanLook, setScanLook] = useState(true);
    const [box, setBox] = useState({ x0: 0.06, y0: 0.06, x1: 0.94, y1: 0.94 });

    const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

    useEffect(() => {
        if (!open || shot) return undefined;
        let cancelled = false;
        setError("");
        setReady(false);
        if (!navigator.mediaDevices?.getUserMedia) { setError("This browser can't open the camera here."); return undefined; }
        navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
            .then((s) => {
                if (cancelled) return s.getTracks().forEach((t) => t.stop());
                streamRef.current = s;
                if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
            })
            .catch(() => setError("Camera access was blocked. Allow camera access, or use your phone's camera app."));
        return () => { cancelled = true; stop(); };
    }, [open, shot]);

    const close = () => { stop(); setShot(null); onClose(); };

    const snap = () => {
        const v = videoRef.current;
        if (!v?.videoWidth) return;
        const c = document.createElement("canvas");
        c.width = v.videoWidth; c.height = v.videoHeight;
        c.getContext("2d").drawImage(v, 0, 0);
        stop();
        setBox({ x0: 0.06, y0: 0.06, x1: 0.94, y1: 0.94 });
        setPreview(c.toDataURL("image/jpeg", 0.8));
        setShot(c);
    };

    const save = () => {
        const sx = Math.round(box.x0 * shot.width), sy = Math.round(box.y0 * shot.height);
        const w = Math.round((box.x1 - box.x0) * shot.width), h = Math.round((box.y1 - box.y0) * shot.height);
        const out = document.createElement("canvas");
        out.width = w; out.height = h;
        const ctx = out.getContext("2d");
        ctx.drawImage(shot, sx, sy, w, h, 0, 0, w, h);
        if (scanLook) enhance(ctx, w, h);
        out.toBlob((blob) => {
            const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
            onCapture(new File([blob], `scan-${stamp}.jpg`, { type: "image/jpeg" }));
            close();
        }, "image/jpeg", 0.88);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="max-w-3xl p-0 bg-black border-0 overflow-hidden [&>button]:text-white [&>button]:opacity-90" data-testid="document-scanner">
                <DialogTitle className="sr-only">Scan document</DialogTitle>
                <div className="relative w-full bg-black flex items-center justify-center min-h-[50vh]">
                    {!shot && !error && <video ref={videoRef} playsInline muted autoPlay onLoadedMetadata={() => setReady(true)} className="w-full max-h-[70vh] object-contain" data-testid="scanner-video" />}
                    {!shot && !error && <div className="pointer-events-none absolute inset-[6%] border-2 border-dashed border-white/60 rounded" />}
                    {shot && (
                        <div ref={wrapRef} className="relative inline-block overflow-hidden">
                            <img src={preview} alt="Captured page" className={`block max-w-full max-h-[70vh] ${scanLook ? "grayscale contrast-150" : ""}`} data-testid="scanner-preview" />
                            <CropBox box={box} setBox={setBox} wrapRef={wrapRef} />
                        </div>
                    )}
                    {error && <div className="p-8 text-center text-white/80 text-sm" data-testid="scanner-error">{error}</div>}
                </div>
                <div className="flex items-center justify-between gap-2 px-4 py-3 bg-ink text-white flex-wrap">
                    <button type="button" onClick={close} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs hover:bg-white/10" data-testid="scanner-cancel"><X className="w-4 h-4" /> Cancel</button>
                    {!shot ? (
                        <>
                            <button type="button" onClick={() => { close(); onNativeCamera(); }} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs hover:bg-white/10" data-testid="scanner-native-camera"><Smartphone className="w-4 h-4" /> Phone camera app</button>
                            {!error && <button type="button" onClick={snap} disabled={!ready} className="w-14 h-14 rounded-full bg-white text-ink flex items-center justify-center ring-4 ring-white/30 disabled:opacity-40" data-testid="scanner-snap" title="Take picture"><Camera className="w-6 h-6" /></button>}
                        </>
                    ) : (
                        <>
                            <label className="inline-flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={scanLook} onChange={(e) => setScanLook(e.target.checked)} className="accent-klein" data-testid="scanner-enhance" /> Scanned look</label>
                            <button type="button" onClick={() => setShot(null)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs hover:bg-white/10" data-testid="scanner-retake"><RotateCcw className="w-4 h-4" /> Retake</button>
                            <button type="button" onClick={save} className="inline-flex items-center gap-1.5 bg-klein px-4 py-2 text-sm" data-testid="scanner-use"><Check className="w-4 h-4" /> Attach scan</button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default DocumentScanner;
